import { FC } from 'react'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Dialog from '@common/Dialog'
import Grid from '@mui/material/Grid'
import CloseIcon from '@mui/icons-material/Close'
import { IconButton } from '@mui/material'
import MuiLink from '@mui/material/Link'
import { Link } from 'react-router-dom'
import { useHeaderAuth } from './useHeaderAuth'
import { AUTH_TYPES } from './constants'
import localStore from '@service/local-store'

const ClinicianSignIn: FC<{
  open: boolean
  setOpen: Function
  url: string
}> = ({ open, setOpen, url }) => {
  const { handleAuthTypeSelection, trackLink, login, role } = useHeaderAuth()

  const handleClose = () => {
    trackLink(
      {
        name: 'close (X)',
        location: 'Header',
        type: 'internal',
        description: 'Dismisses sign in method modal',
      },
      {
        loginStatus: login ? 'logged in' : 'logged out',
        userType: role,
      }
    )
    setOpen(false)
  }

  const handleAuthSelection = (
    authType: string,
    label: string,
    description: string
  ) => {
    trackLink(
      {
        name: label,
        location: 'Signin',
        type: 'internal',
        destinationUrl: url,
        description: description,
      },
      {
        loginStatus: login ? 'logged in' : 'logged out',
        userType: role,
      }
    )
    setOpen(false)
    localStore.set('loginEvent', 'true')
    handleAuthTypeSelection(authType, url)
  }

  return (
    <Dialog
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-clinician-signin"
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth={'lg'}
    >
      <Box sx={{ px: { sm: 1, md: 3 }, py: { xs: 1, sm: 1, md: 1.5 } }}>
        <IconButton
          sx={{ float: 'right' }}
          aria-label={`close`}
          title="Close"
          onClick={handleClose}
        >
          <CloseIcon fontSize={'large'} />
        </IconButton>
      </Box>
      <DialogContent sx={{ pl: { xs: 2, md: 5 }, pr: { xs: 2, md: 5 }, pb: 5 }}>
        <Box>
          <Typography variant={'h2'} color={'primary'}>
            Welcome to the Optum Match Clinician Portal
          </Typography>
          <Typography variant="body1" sx={{ mt: 2.5, mb: 3.5 }}>
            Please choose your sign-in method below.
          </Typography>

          <Grid container spacing={{ xs: 2, md: 5 }} alignItems="stretch">
            <Grid item xs={12} sm={6}>
              <Box
                sx={({ ues }) => ({
                  border: 2,
                  borderRadius: '5px',
                  borderColor: ues.neutral['20'],
                  padding: 3.5,
                  height: { sm: '220px', md: '220px' },
                })}
              >
                <Typography
                  variant={'h4'}
                  sx={({ ues }) => ({
                    color: ues.neutral[100],
                    mb: 2.5,
                  })}
                >
                  UHG Microsoft ID (MSID)
                </Typography>
                <Typography variant={'body1'} sx={{ mt: 2.5, mb: 2.5 }}>
                  Single sign-on for Optum Care clinicians.
                </Typography>

                <Box display={'flex'}>
                  <Button
                    size={'large'}
                    sx={{ mt: 2.7, width: { xs: '100%', md: '50%' } }}
                    variant={'contained'}
                    onClick={() =>
                      handleAuthSelection(
                        AUTH_TYPES.MSID,
                        'om_cl_msid_signin_click',
                        'Click to sign in using MSID'
                      )
                    }
                  >
                    Sign in with MSID
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={({ ues }) => ({
                  border: 2,
                  borderRadius: '5px',
                  borderColor: ues.neutral['20'],
                  padding: 3.5,
                  height: { sm: '220px', md: '220px' },
                })}
              >
                <Typography
                  variant={'h4'}
                  sx={({ ues }) => ({
                    color: ues.neutral[100],
                    mb: 2.5,
                  })}
                >
                  Onehealthcare ID (OHID)
                </Typography>
                <Typography variant={'body1'} sx={{ mt: 2.5, mb: 2.5 }}>
                  For all other clinicians registered to use Optum Match.
                </Typography>

                <Box display={{ xs: 'initial', md: 'flex' }}>
                  <Button
                    sx={{
                      mt: { md: 0.5, sm: 2.7, xs: 2.7, lg: 2.5 },
                      width: { xs: '100%', md: '50%' },
                    }}
                    variant={'contained'}
                    size={'large'}
                    onClick={() =>
                      handleAuthSelection(
                        AUTH_TYPES.OHID,
                        'om_cl_ohid_signin_click',
                        'Click to sign in using OHID'
                      )
                    }
                  >
                    Sign in with OHID
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Typography variant={'body1'} sx={{ textAlign: 'Center', mt: 3.5 }}>
            Need help?
            <MuiLink
              component={Link}
              variant={'body2'}
              to={'/contact-us'}
              sx={({ ues }) => ({
                color: ues.primary['70'],
                textDecoration: 'underline',
                ml: 1,
              })}
              onClick={() => {
                trackLink(
                  {
                    name: 'Contact us (link)',
                    location: 'Clinician modal footer',
                    type: 'internal',
                    destinationUrl: '/contact-us',
                    description:
                      'Opens Contact us page from clinician modal footer',
                  },
                  {
                    loginStatus: login ? 'logged in' : 'logged out',
                    userType: role,
                  }
                )
                setOpen(false)
              }}
            >
              Contact us.
            </MuiLink>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ClinicianSignIn
